"use client"

import { dateLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { activePlanMasters } from "@agentcall/core/master"
import { planMasterType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { addMonths, max, parseISO, startOfDay, startOfMonth } from "date-fns"
import { useEffect } from "react"
import { Check, Repeat } from "react-feather"
import { useForm } from "react-hook-form"
import * as z from "zod"
import type {
  TenantApplicationGet200TenantApplication,
  TenantApplicationGet200TenantApplicationRoom,
} from "@/api/generated/models"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form/form"

// Form schema (same as PlanAddPage)
const planSubscribeFormSchema = z.object({
  planMasterId: z.enum(
    [planMasterType.PLAN_MASTER_ID_STANDARD, planMasterType.PLAN_MASTER_ID_PLUS] as const,
    {
      error: "プランを選択してください",
    }
  ),
  startDate: z.date({
    error: "プラン利用開始日を選択してください",
  }),
  agreement: z.boolean().refine((val) => val === true, {
    message: "承諾が必要です",
  }),
})

export type PlanStartFormData = z.infer<typeof planSubscribeFormSchema>

export interface PlanStartDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: PlanStartFormData) => Promise<void>
  onBack?: () => void
  loading?: boolean
  tenantApplication: TenantApplicationGet200TenantApplication | null
  room: TenantApplicationGet200TenantApplicationRoom | null
}

export function PlanStartDialog({
  open,
  onOpenChange,
  onSubmit,
  onBack,
  loading = false,
  tenantApplication,
  room,
}: PlanStartDialogProps) {
  // 契約開始前にプランを始めると初月無料が契約前に消化されるため、今日と契約開始日の遅い方からしか選べない
  const contractStartDate = tenantApplication?.startDate
  const minStartDate = max([
    startOfDay(new Date()),
    ...(contractStartDate ? [parseISO(contractStartDate)] : []),
  ])
  const minStartDateISO = dateLib.formatDateISO(minStartDate)

  const form = useForm<PlanStartFormData>({
    resolver: zodResolver(planSubscribeFormSchema),
    defaultValues: {
      planMasterId: planMasterType.PLAN_MASTER_ID_PLUS, // Default to Plus Plan
      startDate: new Date(),
      agreement: false,
    },
  })

  // 申込の取得後に契約開始日が分かるので、初期値を下限に合わせる
  useEffect(() => {
    form.setValue("startDate", parseISO(minStartDateISO))
  }, [form, minStartDateISO])

  const handleSubmit = async (data: PlanStartFormData) => {
    await onSubmit(data)
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      onOpenChange(false)
    }
  }

  // Calculate subscription start date (first day of next month from service start date)
  const startDate = form.watch("startDate")
  const subscriptionStartDate = startDate
    ? dateLib.formatDateJapanese(startOfMonth(addMonths(startDate, 1)))
    : ""

  const property = room?.property

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>プランに加入する</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Contract information */}
            <div className="rounded-md bg-slate-100 p-4">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500">物件名</p>
                  <p className="text-sm font-semibold text-primary">{property?.name ?? ""}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500">居室番号</p>
                  <p className="text-sm font-semibold text-primary">{room?.name ?? ""}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500">賃借人氏名</p>
                  <p className="text-sm font-semibold text-primary">
                    {tenantApplication?.name ?? ""}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500">電話番号</p>
                  <p className="text-sm font-semibold text-primary">
                    {tenantApplication
                      ? formatPhone(
                          tenantApplication.phone1,
                          tenantApplication.phone2,
                          tenantApplication.phone3
                        )
                      : ""}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500">メールアドレス</p>
                  <p className="text-sm font-semibold text-primary">
                    {tenantApplication?.email ?? ""}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500">初回契約開始日</p>
                  <p className="text-sm font-semibold text-primary">
                    {tenantApplication?.startDate
                      ? dateLib.formatDateJapanese(tenantApplication.startDate)
                      : ""}
                  </p>
                </div>
              </div>
            </div>

            {/* Plan selection - Plus Plan only (fixed) */}
            <Card>
              <CardContent className="p-4">
                <div className="mb-4 flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-slate-600" />
                  <h3 className="text-base font-semibold text-slate-600">プラン</h3>
                </div>

                {/* プラスプランのみ表示 */}
                {(() => {
                  const plusPlan = activePlanMasters.find(
                    (p) => p.id === planMasterType.PLAN_MASTER_ID_PLUS
                  )
                  if (!plusPlan) return null

                  return (
                    <FormField
                      control={form.control}
                      name="planMasterId"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <input
                              type="hidden"
                              {...field}
                              value={planMasterType.PLAN_MASTER_ID_PLUS}
                            />
                          </FormControl>
                          <Card className="border-primary">
                            <CardContent className="p-4">
                              <div className="space-y-4">
                                <div className="flex items-start justify-between">
                                  <h3 className="text-base font-semibold">{plusPlan.name}</h3>
                                  <p className="text-sm font-semibold">
                                    {plusPlan.monthlyPrice.toLocaleString()}円 (税込) / 月額
                                  </p>
                                </div>
                                <div className="space-y-2">
                                  {plusPlan.features.map((feature: string) => (
                                    <div
                                      key={feature}
                                      className="flex items-center gap-2 font-semibold"
                                    >
                                      <Check className="h-3.5 w-3.5 text-primary" />
                                      <p className="text-xs text-slate-600">{feature}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </FormItem>
                      )}
                    />
                  )
                })()}
              </CardContent>
            </Card>

            {/* Service start date */}
            <Card>
              <CardContent className="p-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>プラン利用開始日</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="日付を選択"
                          fromDate={minStartDate}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Subscription start date display */}
            <div className="rounded-md bg-slate-100 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-500">ご請求開始日</p>
                <p className="text-sm font-semibold text-primary">{subscriptionStartDate}</p>
              </div>
            </div>

            {/* Agreement checkbox */}
            <Card>
              <CardContent className="p-4">
                <FormField
                  control={form.control}
                  name="agreement"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">
                          当フォームの内容で、サービス料金が発生することに承諾します。
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
                戻る
              </Button>
              <Button type="submit" disabled={loading || !form.formState.isValid} loading={loading}>
                プランに加入する
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
